/**
 * Agency Portfolio — the operator's home: "How is the whole book of business
 * doing?" Everyone's numbers in one place, sliceable, with an attention feed
 * that says what to do next.
 *
 *  - Rollup tiles: portfolio revenue / CM / blended MER / customers / subs.
 *  - "Needs your attention": data-health first (a broken pipeline outranks a
 *    scary metric — the metric is usually lying when the pipeline is broken).
 *  - Client table: one row per client, the profit levers as columns, worst
 *    health first, totals + medians footer.
 *  - Marketing stats: medians across real clients (demo excluded), with an
 *    anonymize toggle + copy/CSV for dropping straight into content.
 *
 * Operator-gated server-side; the page also checks and explains.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useToast } from "../lib/toast.js";
import { Card, CardHead, Tile, moneyFmt } from "../components/cards.js";
import { Segmented } from "../components/Segmented.js";
import { useAccountChange } from "../lib/account-context.js";

type PeriodKey = "30d" | "90d" | "365d" | "ytd" | "all";

interface ClientAlert {
  accountId: string;
  clientName: string;
  kind: "data" | "setup" | "performance";
  severity: 1 | 2 | 3;
  title: string;
  detail: string;
  nextMove: string;
}

interface PortfolioRow {
  accountId: string;
  name: string;
  isDemo: boolean;
  isSample?: boolean;
  revenue: number;
  revenueChangePct: number | null;
  cm: number | null;
  cmPercent: number | null;
  cmPercentPrev: number | null;
  hasCosts: boolean;
  orders: number;
  customers: number;
  aov: number | null;
  mer: number | null;
  adSpend: number;
  activeSubs: number;
  subsNetAdds30: number;
  onboardingCompleted: boolean;
  onboardingStep: number;
  costsCoveragePct: number | null;
  connectionsActive: number;
  connectionsBroken: number;
  lastSyncAt: string | null;
  syncStale: boolean;
  failedJobs48h: number;
  pendingIngestFailures: number;
  health: "good" | "warn" | "bad";
}

interface Portfolio {
  periodKey: PeriodKey;
  periodLabel: string;
  rollup: {
    clients: number;
    revenue: number;
    revenueChangePct: number | null;
    cm: number;
    cmClients: number;
    orders: number;
    customers: number;
    adSpend: number;
    mer: number | null;
    activeSubs: number;
  };
  medians: {
    revenue: number | null;
    cmPercent: number | null;
    mer: number | null;
    aov: number | null;
    revenueChangePct: number | null;
  };
  rows: PortfolioRow[];
  alerts: ClientAlert[];
  stuck: StuckClient[];
  openBugs: { total: number; critical: number; high: number; fixReady: number };
  failedAccounts: Array<{ accountId: string; name: string }>;
}

interface ProofCard {
  accountId: string;
  clientName: string;
  monthsSpanned: number;
  baselineMonth: string;
  latestMonth: string;
  cmPctBaseline: number;
  cmPctLatest: number;
  cmPctDeltaPts: number;
}

interface ProofSummaryPayload {
  includedClients: number;
  totalClients: number;
  medianCmPctDeltaPts: number | null;
  medianMonthsSpanned: number | null;
  improvedCount: number;
  cards: ProofCard[];
  excluded: Array<{ accountId: string; clientName: string; reason: string }>;
}

interface StuckClient {
  accountId: string;
  clientName: string;
  signals: Array<{ key: string; label: string; detail: string; sinceDays: number | null }>;
  daysStuck: number | null;
  contactEmail: string | null;
}

const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string }> = [
  { value: "30d", label: "30D" },
  { value: "90d", label: "90D" },
  { value: "365d", label: "1Y" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All" },
];

const pctFmt = (v: number | null, digits = 0): string => (v === null ? "—" : `${(v * 100).toFixed(digits)}%`);
const deltaFmt = (v: number | null): string | undefined => (v === null ? undefined : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(0)}% vs prior`);
const merFmt = (v: number | null): string => (v === null ? "—" : `${v.toFixed(2)}×`);

export default function AgencyPortfolioPage(): React.ReactElement {
  const api = useApiClient();
  const changeAccount = useAccountChange();
  const toast = useToast();
  const [period, setPeriod] = useState<PeriodKey>("90d");
  const [anonymize, setAnonymize] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["account", "agency-status"],
    queryFn: () => api.get<{ isOperator: boolean }>("/account/agency-status"),
  });
  const isOperator = status.data?.isOperator ?? false;

  const q = useQuery({
    queryKey: ["admin", "portfolio", period],
    enabled: isOperator,
    queryFn: () => api.get<Portfolio>("/admin/portfolio", { period }),
    staleTime: 60_000,
  });
  const p = q.data;

  // Anonymized labels are stable within the view: sorted by revenue desc →
  // Client A is always the biggest, matching how the stat reads in content.
  const anonLabels = useMemo(() => {
    const map = new Map<string, string>();
    if (!p) return map;
    const real = p.rows.filter((r) => !(r.isSample ?? r.isDemo)).slice().sort((a, b) => b.revenue - a.revenue);
    real.forEach((r, i) => map.set(r.accountId, `Client ${String.fromCharCode(65 + (i % 26))}`));
    return map;
  }, [p]);
  const displayName = (row: PortfolioRow): string => (anonymize && !(row.isSample ?? row.isDemo) ? anonLabels.get(row.accountId) ?? "Client" : row.name);

  const switchTo = async (accountId: string): Promise<void> => {
    setSwitching(accountId);
    try {
      await changeAccount({
        path: "/account/switch",
        body: { accountId },
        targetAccountId: accountId,
        destination: "/dashboard",
      });
    } catch {
      setSwitching(null);
      toast.error("Couldn't switch into that account.");
    }
  };

  const copyStats = async (): Promise<void> => {
    if (!p) return;
    const lines = [
      `Across ${p.rollup.clients} client brands (${p.periodLabel}):`,
      `- Portfolio revenue: ${moneyFmt(p.rollup.revenue)}${p.rollup.revenueChangePct !== null ? ` (${deltaFmt(p.rollup.revenueChangePct)})` : ""}`,
      p.medians.revenue !== null ? `- Median revenue per brand: ${moneyFmt(p.medians.revenue)}` : null,
      p.medians.cmPercent !== null ? `- Median contribution margin: ${pctFmt(p.medians.cmPercent, 1)}` : null,
      p.medians.mer !== null ? `- Median MER: ${merFmt(p.medians.mer)}` : null,
      p.medians.aov !== null ? `- Median AOV: ${moneyFmt(p.medians.aov)}` : null,
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Copied — ready to paste into your content.");
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  const downloadCsv = (): void => {
    if (!p) return;
    const rows = p.rows.filter((r) => !(r.isSample ?? r.isDemo));
    const head = ["Client", "Revenue", "Revenue change %", "CM $", "CM %", "MER", "AOV", "Orders", "Customers", "Active subs", "Subs net 30d"];
    const csv = [
      head.join(","),
      ...rows.map((r) =>
        [
          JSON.stringify(displayName(r)),
          r.revenue.toFixed(2),
          r.revenueChangePct === null ? "" : (r.revenueChangePct * 100).toFixed(1),
          r.cm === null ? "" : r.cm.toFixed(2),
          r.cmPercent === null ? "" : (r.cmPercent * 100).toFixed(1),
          r.mer === null ? "" : r.mer.toFixed(2),
          r.aov === null ? "" : r.aov.toFixed(2),
          r.orders,
          r.customers,
          r.activeSubs,
          r.subsNetAdds30,
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `portfolio-${p.periodKey}${anonymize ? "-anonymized" : ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (status.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (!isOperator) {
    return (
      <div>
        <h1 style={{ margin: 0, fontSize: 24 }}>Agency</h1>
        <p style={{ color: "var(--body)", marginTop: 10 }}>This area is only available to agency operators.</p>
      </div>
    );
  }

  const proofQ = useQuery({
    queryKey: ["admin", "proof"],
    queryFn: () => api.get<ProofSummaryPayload>("/admin/proof"),
    staleTime: 5 * 60_000,
  });
  const proof = proofQ.data;

  const alerts = p?.alerts ?? [];
  // Founder 2026-07-05: attention = BUGS (they live in Feedback & Fixes);
  // client-health problems moved into the stuck list; performance stays its
  // own small feed. DATA alerts render FIRST (2026-07-11: they were computed
  // and thrown away — Everbella's failing jobs showed only as an unexplained
  // yellow dot while the client stared at a red "refresh failed" pill).
  const dataAlerts = alerts.filter((a) => a.kind === "data");
  const performanceAlerts = alerts.filter((a) => a.kind === "performance");
  const stuck = p?.stuck ?? [];
  const openBugs = p?.openBugs ?? { total: 0, critical: 0, high: 0, fixReady: 0 };
  const rows = p?.rows ?? [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={kicker}>Agency</div>
          <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>How is the whole book of business doing?</h1>
          <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 780, lineHeight: 1.5, marginTop: 0 }}>
            Every client's numbers, one screen. The feed below tells you who needs attention and what to do — data problems first,
            because a broken sync makes every metric under it lie.
          </p>
        </div>
        <div style={{ marginTop: 4 }}>
          <Segmented<PeriodKey> value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
        </div>
      </div>

      {q.isLoading && <p style={{ marginTop: 20, color: "var(--body)" }}>Pulling every client's numbers…</p>}
      {q.isError && <p style={{ marginTop: 20, color: "var(--bad)" }}>Couldn't load the portfolio. Refresh to retry.</p>}

      {p && (
        <>
          {p.failedAccounts.length > 0 && (
            <div style={warnStrip}>
              Couldn't compute numbers for: {p.failedAccounts.map((f) => f.name).join(", ")} — their rows are missing from the totals below.
            </div>
          )}

          {/* Rollup */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginTop: 18 }}>
            <Tile
              title={`Portfolio revenue · ${p.periodLabel}`}
              value={moneyFmt(p.rollup.revenue)}
              sub={`${p.rollup.clients} client${p.rollup.clients === 1 ? "" : "s"} · ${p.rollup.orders.toLocaleString()} orders`}
              delta={p.rollup.revenueChangePct ?? undefined}
              explain={[
                "What: every real client's revenue added together for this window (the seeded demo account is excluded).",
                "How: each client's number is computed by the same engine their own dashboard uses — this can never disagree with what they see.",
                "Note: revenue basis follows each account's saved revenue adjustments (refunds/tax/shipping).",
              ]}
            />
            <Tile
              title="Contribution margin"
              value={p.rollup.cmClients > 0 ? moneyFmt(p.rollup.cm) : "—"}
              sub={
                p.rollup.cmClients > 0
                  ? `across ${p.rollup.cmClients} of ${p.rollup.clients} clients ready for profit reporting`
                  : "no clients are ready for profit reporting yet"
              }
              explain={[
                "What: what remains after product costs and other variable costs, across clients whose costs and review are complete.",
                "How: add the contribution margin from clients whose own dashboard has profit reporting unlocked. Test and demo accounts never count.",
                "Example: if only 2 of 3 clients have approved costs, this total covers those 2 clients only.",
                "Note: entering some costs is not enough. Missing expenses, source limitations, or an unfinished review keep a client out.",
              ]}
            />
            <Tile
              title="Blended MER"
              value={merFmt(p.rollup.mer)}
              sub={p.rollup.adSpend > 0 ? `${moneyFmt(p.rollup.adSpend)} acquisition spend` : "no ad spend entered yet"}
              explain={[
                "What: portfolio marketing efficiency — gross revenue ÷ total acquisition spend, across clients that track spend.",
                "How: spend = manual ad spend + affiliate commissions, prorated to the window; the revenue side is gross-of-refunds (the MER convention).",
                "Note: clients with zero spend entered are excluded from both sides so the ratio stays honest.",
              ]}
            />
            <Tile
              title="Customers"
              value={p.rollup.customers.toLocaleString()}
              sub="bought in this window"
              explain={[
                "What: unique buying customers across all real clients in this window.",
                "Note: customers are counted per client and summed — one person buying from two brands counts in each brand.",
              ]}
            />
            <Tile
              title="Active subscriptions"
              value={p.rollup.activeSubs.toLocaleString()}
              sub="right now, all clients"
              explain={[
                "What: active recurring plans across the portfolio, as of right now (not window-scoped).",
                "Note: per-client detail (churn, net adds, rebill calendar) lives on each client's Subscriptions page.",
              ]}
            />
          </div>

          {/* Data problems FIRST — a broken pipeline makes every metric under
              it lie, so it outranks bugs and performance moves. */}
          {dataAlerts.length > 0 && (
            <Card style={{ marginTop: 16 }}>
              <CardHead
                title={`Data problems · ${dataAlerts.length}`}
                explain={[
                  "What: broken connections, stale syncs, failed sync jobs, and records stuck in the retry queue — per client.",
                  "How: the same signals that turn a client's own sync pill red. If a client can see a data error, it shows here too.",
                  "Note: the Issues page has the full feed with the raw error messages.",
                ]}
              >
                <Link to="/agency/issues" style={{ color: "var(--accent)", fontSize: 13 }}>
                  See all issues →
                </Link>
              </CardHead>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {dataAlerts.map((a, i) => (
                  <div key={`${a.accountId}-${i}`} style={alertRow}>
                    <span style={{ ...sevDot, background: a.severity === 1 ? "var(--bad)" : "#E5A83B" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, color: "var(--text)" }}>
                        <b>{anonymize ? anonLabels.get(a.accountId) ?? a.clientName : a.clientName}</b> — {a.title}
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 2 }}>{a.detail}</div>
                      <div style={{ fontSize: 12.5, color: "var(--accent)", marginTop: 2 }}>
                        <b>Next move:</b> {a.nextMove}
                      </div>
                    </div>
                    <button type="button" style={ghostBtn} disabled={switching !== null} onClick={() => void switchTo(a.accountId)}>
                      {switching === a.accountId ? "Switching…" : "Switch in →"}
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Needs your attention = open BUGS (they live in Feedback & Fixes) */}
          <Card style={{ marginTop: 16 }}>
            <CardHead
              title={`Needs your attention · ${openBugs.total} open bug${openBugs.total === 1 ? "" : "s"}`}
              explain={[
                "What: open bug reports across all clients — the things actively broken for someone.",
                "How: bugs live in Feedback & Fixes (triage, AI fix runs, your approve button); this is the pointer so nothing rots unseen.",
                "Note: client-health problems (stuck clients, broken data) have their own section below — they're churn risks, not bugs.",
              ]}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginTop: 6 }}>
              {openBugs.total === 0 ? (
                <p style={{ color: "var(--body)", fontSize: 13.5, margin: 0 }}>No open bugs. That's the goal.</p>
              ) : (
                <>
                  {openBugs.critical > 0 && <span style={{ color: "var(--bad)", fontSize: 13.5 }}><b>{openBugs.critical}</b> critical</span>}
                  {openBugs.high > 0 && <span style={{ color: "#E5A83B", fontSize: 13.5 }}><b>{openBugs.high}</b> high</span>}
                  {openBugs.fixReady > 0 && <span style={{ color: "var(--good)", fontSize: 13.5 }}><b>{openBugs.fixReady}</b> fix{openBugs.fixReady === 1 ? "" : "es"} ready for your approval</span>}
                </>
              )}
              <Link to="/agency/feedback" style={{ color: "var(--accent)", fontSize: 13.5, marginLeft: "auto" }}>
                Open Feedback &amp; Fixes →
              </Link>
            </div>
          </Card>

          {/* Clients getting stuck — the churn-risk reach-out list */}
          <Card style={{ marginTop: 16 }}>
            <CardHead
              title={`Clients getting stuck${stuck.length ? ` · ${stuck.length}` : ""}`}
              explain={[
                "What: clients showing churn-risk signals — never logged in, went quiet, stalled onboarding, nothing connected, data broken, or still stuck on revenue-only numbers.",
                "How: sorted by how long they've been stuck; each shows at most its two strongest signals. Reach out before they drift away — this list exists to start conversations, not to shame anyone.",
                "Note: login tracking started Jul 5 — 'hasn't logged in' means not seen since then.",
              ]}
            />
            {stuck.length === 0 ? (
              <p style={{ color: "var(--body)", fontSize: 13.5, margin: "6px 0 2px" }}>
                Nobody's stuck — every client is active, connected, and seeing real profit numbers.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {stuck.map((s) => (
                  <div key={s.accountId} style={alertRow}>
                    <span style={{ ...sevDot, background: s.signals[0]?.key === "sync_broken" ? "var(--bad)" : "#E5A83B" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, color: "var(--text)" }}>
                        <b>{anonymize ? anonLabels.get(s.accountId) ?? s.clientName : s.clientName}</b>
                        {" — "}
                        {s.signals.map((sig) => sig.label).join(" · ")}
                        {s.daysStuck !== null && <span style={{ color: "var(--body)" }}> · {s.daysStuck}d</span>}
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 2 }}>{s.signals[0]?.detail}</div>
                    </div>
                    {s.contactEmail && !anonymize && (
                      <button
                        type="button"
                        style={ghostBtn}
                        onClick={() => {
                          void navigator.clipboard.writeText(s.contactEmail ?? "");
                        }}
                        title={s.contactEmail}
                      >
                        Copy email
                      </button>
                    )}
                    <button type="button" style={ghostBtn} disabled={switching !== null} onClick={() => void switchTo(s.accountId)}>
                      {switching === s.accountId ? "Switching…" : "Switch in →"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Performance flags — metric moves worth a look (data problems live above) */}
          {performanceAlerts.length > 0 && (
            <Card style={{ marginTop: 16 }}>
              <CardHead
                title={`Performance flags · ${performanceAlerts.length}`}
                explain={[
                  "What: notable metric moves (revenue drops, margin slides) on clients whose data is healthy.",
                  "How: suppressed automatically for any client whose pipeline is broken — a broken sync looks like a revenue crash, and chasing phantom drops is the classic wasted hour.",
                ]}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {performanceAlerts.map((a, i) => (
                  <div key={`${a.accountId}-${i}`} style={alertRow}>
                    <span style={{ ...sevDot, background: a.severity === 1 ? "var(--bad)" : a.severity === 2 ? "#E5A83B" : "var(--body-faint)" }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, color: "var(--text)" }}>
                        <b>{anonymize ? anonLabels.get(a.accountId) ?? a.clientName : a.clientName}</b> — {a.title}
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 2 }}>{a.detail}</div>
                      <div style={{ fontSize: 12.5, color: "var(--accent)", marginTop: 2 }}>
                        <b>Next move:</b> {a.nextMove}
                      </div>
                    </div>
                    <button type="button" style={ghostBtn} disabled={switching !== null} onClick={() => void switchTo(a.accountId)}>
                      {switching === a.accountId ? "Switching…" : "Switch in →"}
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Client table */}
          <Card style={{ marginTop: 16 }}>
            <CardHead
              title="Clients, side by side"
              explain={[
                "What: one row per client — revenue, margin, marketing efficiency, subscriptions, and data freshness in the same window.",
                "How: sorted worst-health-first so 'who's slipping' needs zero clicks; the footer shows totals and medians (medians resist one big client skewing the 'typical' number).",
                "Note: the demo account is listed but excluded from totals, medians and marketing stats.",
              ]}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--body)", cursor: "pointer" }}>
                <input type="checkbox" checked={anonymize} onChange={(e) => setAnonymize(e.target.checked)} />
                Anonymize names
              </label>
            </CardHead>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
                <thead>
                  <tr>
                    {["Client", "Revenue", "CM %", "MER", "AOV", "Customers", "Subs ±30d", "Data", ""].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.accountId} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span style={{ ...healthDot, background: r.health === "bad" ? "var(--bad)" : r.health === "warn" ? "#E5A83B" : "var(--good)" }} />
                          <span style={{ fontWeight: 600, color: "var(--text)" }}>{displayName(r)}</span>
                          {(r.isSample ?? r.isDemo) && <span style={demoChip}>{r.isDemo ? "demo" : "test data"}</span>}
                          {!r.onboardingCompleted && !(r.isSample ?? r.isDemo) && <span style={setupChip}>setting up · step {r.onboardingStep}</span>}
                        </span>
                      </td>
                      <td style={tdNum}>
                        {moneyFmt(r.revenue)}
                        {r.revenueChangePct !== null && (
                          <div style={{ fontSize: 11, color: r.revenueChangePct >= 0 ? "var(--good)" : "var(--bad)" }}>
                            {r.revenueChangePct >= 0 ? "+" : ""}
                            {(r.revenueChangePct * 100).toFixed(0)}%
                          </div>
                        )}
                      </td>
                      <td style={tdNum} title={r.hasCosts ? undefined : "Costs and other expenses still need to be finished and reviewed. Profit reporting stays off until then."}>
                        {pctFmt(r.cmPercent, 1)}
                        {r.cmPercent !== null && r.cmPercentPrev !== null && (
                          <div style={{ fontSize: 11, color: r.cmPercent >= r.cmPercentPrev ? "var(--good)" : "var(--bad)" }}>
                            {r.cmPercent >= r.cmPercentPrev ? "+" : ""}
                            {((r.cmPercent - r.cmPercentPrev) * 100).toFixed(1)} pts
                          </div>
                        )}
                      </td>
                      <td style={tdNum}>{merFmt(r.mer)}</td>
                      <td style={tdNum}>{r.aov === null ? "—" : moneyFmt(r.aov)}</td>
                      <td style={tdNum}>{r.customers.toLocaleString()}</td>
                      <td style={tdNum}>
                        {r.activeSubs > 0 || r.subsNetAdds30 !== 0 ? (
                          <span style={{ color: r.subsNetAdds30 < 0 ? "var(--bad)" : "var(--good)" }}>
                            {r.subsNetAdds30 >= 0 ? "+" : ""}
                            {r.subsNetAdds30}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={td}>
                        {r.connectionsBroken > 0 ? (
                          <span style={{ color: "var(--bad)", fontSize: 12 }}>⚠ {r.connectionsBroken} broken</span>
                        ) : r.syncStale ? (
                          <span style={{ color: "#B07E18", fontSize: 12 }}>stale</span>
                        ) : r.lastSyncAt ? (
                          <span style={{ color: "var(--body)", fontSize: 12 }}>{freshness(r.lastSyncAt)}</span>
                        ) : (
                          <span style={{ color: "var(--body-faint)", fontSize: 12 }}>no sync yet</span>
                        )}
                        {/* Job/ingest failures were invisible here — a client whose
                            store pull succeeds but whose recompute keeps failing
                            showed a benign "2h ago" (2026-07-11). */}
                        {r.failedJobs48h > 0 && (
                          <span style={{ color: "var(--bad)", fontSize: 12 }}> · {r.failedJobs48h} failed job{r.failedJobs48h > 1 ? "s" : ""}</span>
                        )}
                        {r.pendingIngestFailures > 0 && (
                          <span style={{ color: "#B07E18", fontSize: 12 }}> · {r.pendingIngestFailures} record{r.pendingIngestFailures > 1 ? "s" : ""} stuck</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button type="button" style={ghostBtn} disabled={switching !== null} onClick={() => void switchTo(r.accountId)}>
                          {switching === r.accountId ? "…" : "Open →"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "2px solid var(--border)" }}>
                    <td style={{ ...td, fontWeight: 700, color: "var(--text)" }}>Totals (real clients)</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{moneyFmt(p.rollup.revenue)}</td>
                    <td style={tdNum}>{p.rollup.cmClients > 0 ? moneyFmt(p.rollup.cm) : "—"}</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{merFmt(p.rollup.mer)}</td>
                    <td style={tdNum}>—</td>
                    <td style={{ ...tdNum, fontWeight: 700 }}>{p.rollup.customers.toLocaleString()}</td>
                    <td style={tdNum}>—</td>
                    <td style={td} />
                    <td style={td} />
                  </tr>
                  <tr>
                    <td style={{ ...td, fontWeight: 600, color: "var(--body)" }}>Medians</td>
                    <td style={tdNum}>{p.medians.revenue === null ? "—" : moneyFmt(p.medians.revenue)}</td>
                    <td style={tdNum}>{pctFmt(p.medians.cmPercent, 1)}</td>
                    <td style={tdNum}>{merFmt(p.medians.mer)}</td>
                    <td style={tdNum}>{p.medians.aov === null ? "—" : moneyFmt(p.medians.aov)}</td>
                    <td style={tdNum}>—</td>
                    <td style={tdNum}>—</td>
                    <td style={td} />
                    <td style={td} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {/* Marketing slicer */}
          <Card style={{ marginTop: 16 }}>
            <CardHead
              title="Marketing stats — your book as proof"
              explain={[
                "What: the aggregate numbers you can quote in content and sales conversations — 'across our client brands…'.",
                "How: medians across real clients only (demo excluded); medians because one whale client shouldn't define 'typical'.",
                "Note: flip on Anonymize before screenshotting — names become Client A/B/C (biggest = A) here and in the table above.",
              ]}
            >
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" style={ghostBtn} onClick={() => void copyStats()}>Copy as text</button>
                <button type="button" style={ghostBtn} onClick={downloadCsv}>Download CSV</button>
              </div>
            </CardHead>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginTop: 6 }}>
              {[
                { label: "Median revenue / brand", value: p.medians.revenue === null ? "—" : moneyFmt(p.medians.revenue) },
                { label: "Median contribution margin", value: pctFmt(p.medians.cmPercent, 1) },
                { label: "Median MER", value: merFmt(p.medians.mer) },
                { label: "Median AOV", value: p.medians.aov === null ? "—" : moneyFmt(p.medians.aov) },
                { label: "Median revenue growth", value: p.medians.revenueChangePct === null ? "—" : `${p.medians.revenueChangePct >= 0 ? "+" : ""}${(p.medians.revenueChangePct * 100).toFixed(0)}%` },
              ].map((s) => (
                <div key={s.label} style={statBox}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "var(--num)" }} className="dd-num">{s.value}</div>
                  <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {p.rollup.clients < 3 && (
              <p style={{ fontSize: 12, color: "var(--body-faint)", margin: "10px 0 0" }}>
                With {p.rollup.clients} real client{p.rollup.clients === 1 ? "" : "s"} connected these medians are thin — they get quotable as the book grows.
              </p>
            )}
          </Card>

          {/* Proof for marketing (founder 2026-07-05: "our customers get X
              more profitable over X months") — screenshot-ready cards. */}
          <Card style={{ marginTop: 16 }}>
            <CardHead
              title="Proof: are clients getting more profitable?"
              explain={[
                "What: each client's contribution-margin percentage, first full month vs latest full month — the honest before/after.",
                "How: the same profit calculation as the client dashboard, including costs and refunds. It excludes partial months, unfinished cost reviews, and clients with fewer than two full months of data.",
                "Note: quote the MEDIAN, and say 'contribution-margin points' — that's the claim that survives scrutiny.",
              ]}
            />
            {!proof || proof.includedClients === 0 ? (
              <p style={{ color: "var(--body)", fontSize: 13.5, margin: "6px 0 2px" }}>
                No provable trends yet. Clients need a finished cost review and at least two full months of data.
                {proof && proof.excluded.length > 0 && ` (${proof.excluded.length} client${proof.excluded.length === 1 ? "" : "s"} excluded so far.)`}
              </p>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
                  <div style={{ ...statBox, borderColor: "var(--accent)" }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--num)" }} className="dd-num">
                      {proof.medianCmPctDeltaPts === null ? "—" : `${proof.medianCmPctDeltaPts >= 0 ? "+" : ""}${(proof.medianCmPctDeltaPts * 100).toFixed(1)} pts`}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                      median contribution-margin change over ~{proof.medianMonthsSpanned ?? "—"} months ({proof.includedClients} of {proof.totalClients} clients measurable)
                    </div>
                  </div>
                  <div style={statBox}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--num)" }} className="dd-num">
                      {proof.improvedCount}/{proof.includedClients}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>clients more profitable than their first month</div>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginTop: 14 }}>
                  {proof.cards.map((c) => (
                    <div key={c.accountId} style={{ ...statBox, textAlign: "left" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                        {anonymize ? anonLabels.get(c.accountId) ?? c.clientName : c.clientName}
                      </div>
                      <div style={{ fontSize: 19, fontWeight: 700, marginTop: 4, color: c.cmPctDeltaPts >= 0 ? "var(--good)" : "var(--bad)" }} className="dd-num">
                        {(c.cmPctBaseline * 100).toFixed(1)}% → {(c.cmPctLatest * 100).toFixed(1)}%
                      </div>
                      <div style={{ fontSize: 12, color: "var(--body)", marginTop: 2 }}>
                        contribution margin, {c.baselineMonth} → {c.latestMonth} ({c.monthsSpanned} months)
                      </div>
                    </div>
                  ))}
                </div>
                {proof.excluded.length > 0 && (
                  <p style={{ fontSize: 12, color: "var(--body-faint)", margin: "10px 0 0" }}>
                    Not yet measurable: {proof.excluded.map((e) => `${anonymize ? anonLabels.get(e.accountId) ?? e.clientName : e.clientName} (${e.reason})`).join("; ")}.
                  </p>
                )}
              </>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function freshness(iso: string): string {
  const hours = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return "synced <1h ago";
  if (hours < 48) return `synced ${hours}h ago`;
  return `synced ${Math.round(hours / 24)}d ago`;
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const warnStrip: React.CSSProperties = {
  marginTop: 14, padding: "9px 13px", fontSize: 12.5, color: "var(--text)", background: "color-mix(in srgb, var(--bad) 9%, transparent)",
  border: "1px solid color-mix(in srgb, var(--bad) 30%, var(--border))", borderRadius: "var(--r-sm)",
};
const alertRow: React.CSSProperties = { display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: "var(--surface-2)", borderRadius: "var(--r-sm)", border: "1px solid var(--border)" };
const sevDot: React.CSSProperties = { width: 9, height: 9, borderRadius: "50%", flexShrink: 0, marginTop: 5 };
const healthDot: React.CSSProperties = { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 };
const demoChip: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, padding: "1px 7px", borderRadius: 9, background: "var(--surface-2)", color: "var(--body)", border: "1px solid var(--border)" };
const setupChip: React.CSSProperties = { fontSize: 10.5, fontWeight: 600, padding: "1px 7px", borderRadius: 9, background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent)" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--body-faint)", fontWeight: 600, whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "9px 10px", color: "var(--body)", verticalAlign: "top" };
const tdNum: React.CSSProperties = { ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
const ghostBtn: React.CSSProperties = {
  background: "transparent", border: "1px solid var(--border)", color: "var(--accent)", borderRadius: "var(--r-sm)",
  padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};
const statBox: React.CSSProperties = { padding: "12px 14px", background: "var(--surface-2)", borderRadius: "var(--r-sm)", border: "1px solid var(--border)" };
